/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquare } from '@fortawesome/free-regular-svg-icons';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faCheckSquare } from '@fortawesome/pro-solid-svg-icons';
import { validEmail } from 'js/utils';
import Alert from 'js/components/alert/alert';
import Box from 'js/components/box/box';
import Button from 'js/components/button/button';
import Checkbox from 'js/components/checkbox/checkbox';
import Col from 'js/components/grid/column';
import Row from 'js/components/grid/row';
import Input from 'js/components/input/input';
import Modal from 'js/components/modal/modal';
import Scrollable from 'js/components/scrollable/scrollable';
import SectionTitle from 'js/components/section-title/section-title';
import Spinner from 'js/components/spinner/spinner';
import logo from 'media/images/logos/logo.svg';
import ReportExample from './images/report-example.png';
import steps from '../enums/steps.enum';
import styles from '../quick-start.module.scss';
import reportExamplePdf from './report-example.pdf';

const cx = classNames.bind(styles);

const LinkButton = ({ children, onClick }) => (
    <Link to="#" tabIndex={-1} className={styles.link} onClick={onClick}>
        {children}
    </Link>
);

const ReportCheckButton = ({ checked, onClick }) => (
    <Button theme="custom" size="auto" onClick={onClick}>
        <div className={styles['report-check-button']}>
            <Row alignItems="center" justifyContent="flex-start" gap="base">
                <Col alignSelf="flex-start">
                    <div className={styles.label}>
                        <span className={styles.check}>
                            <FontAwesomeIcon
                                size="lg"
                                icon={checked ? faCheckSquare : faSquare}
                            />
                        </span>
                        Want to receive a free insights report?
                    </div>
                    <div className={styles.message}>
                        <Link
                            to={reportExamplePdf}
                            target="_blank"
                            tabIndex={-1}
                            download="4D Report Example.pdf"
                            className={styles.link}
                            onClick={(ev) => {
                                ev.stopPropagation();
                            }}
                        >
                            Click here
                        </Link>{' '}
                        for an example report
                    </div>
                </Col>
                <Col span={12} spanMd="auto">
                    <img src={ReportExample} alt="4D report" height={100} />
                </Col>
            </Row>
        </div>
    </Button>
);

const TermsModal = ({ accepted, children, onAccept, onClose }) => {
    const [isBottom, setIsBottom] = useState(false);
    const acceptButtonRef = useRef();
    const scrollableRef = useRef();

    useEffect(() => {
        if (isBottom && acceptButtonRef.current) {
            acceptButtonRef.current.focus();
        }
    }, [isBottom]);

    const scrollDown = () => {
        const scrollable = scrollableRef.current;
        scrollable.scrollTop += scrollable.scrollHeight;
    };

    return (
        <Modal title="Terms and Conditions" onClose={onClose}>
            <div className={styles['terms-modal']}>
                <div className={styles.content}>
                    <Scrollable
                        ref={scrollableRef}
                        hideScrollbar
                        focusOnShow
                        onBottom={setIsBottom}
                    >
                        <img className={styles.logo} src={logo} alt="4D" />
                        <div className={styles.subtitle}>
                            Before using 4D you will need to agree to the Terms
                            and Conditions of the platform.
                        </div>

                        {children}
                    </Scrollable>
                </div>

                <div
                    className={classNames(
                        cx({ buttons: true, bottom: isBottom }),
                    )}
                >
                    <Button
                        square
                        className={styles['scroll-button']}
                        onClick={scrollDown}
                    >
                        <FontAwesomeIcon icon={faChevronDown} />
                    </Button>
                    {!accepted && (
                        <Button
                            disabled={!isBottom}
                            ref={acceptButtonRef}
                            className={styles['accept-button']}
                            onClick={onAccept}
                        >
                            Agree
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

const ActivateStep = ({
    form,
    errors,
    submitting,
    termsAndConditions,
    onFormChange,
    onStepChange,
    onSubmit,
}) => {
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);

    const fields = ['contact_name', 'contact_company', 'contact_email'];
    const fieldErrors = { ...errors };
    Object.keys(fieldErrors).forEach(
        (field) => !fields.includes(field) && delete fieldErrors[field],
    );
    const hasFieldError = Object.keys(fieldErrors).length > 0;
    const hasOtherError =
        Object.keys(errors).length - Object.keys(fieldErrors).length > 0;

    const changeField = (name, value) =>
        onFormChange({ ...form, [name]: value });

    const trimField = (name) =>
        onFormChange({ ...form, [name]: form[name].trim() });

    return (
        <>
            <SectionTitle>
                You’re nearly there, just a few personal details
            </SectionTitle>

            <Box margin={['large', 0, 'base']}>
                <Input
                    label="Name"
                    value={form.contact_name}
                    maxLength={50}
                    disabled={submitting}
                    hasError={!!errors.contact_name}
                    errorMessage={<p>{errors.contact_name}</p>}
                    focusOnShow
                    onChange={(ev) =>
                        changeField('contact_name', ev.target.value)
                    }
                    onBlur={() => trimField('contact_name')}
                />
            </Box>

            <Box margin={['base', 0]}>
                <Input
                    label="Company Name"
                    value={form.contact_company}
                    maxLength={50}
                    disabled={submitting}
                    hasError={!!errors.contact_company}
                    errorMessage={<p>{errors.contact_company}</p>}
                    onChange={(ev) =>
                        changeField('contact_company', ev.target.value)
                    }
                    onBlur={() => trimField('contact_company')}
                />
            </Box>

            <Box margin={['base', 0]}>
                <Input
                    label="Your Company Email"
                    hint={
                        <p>
                            We’ll send your context details to this email
                            address
                        </p>
                    }
                    value={form.contact_email}
                    maxLength={150}
                    disabled={submitting}
                    hasError={!!errors.contact_email}
                    errorMessage={<p>{errors.contact_email}</p>}
                    onChange={(ev) =>
                        changeField('contact_email', ev.target.value)
                    }
                    onBlur={() => trimField('contact_email')}
                />
            </Box>

            <Box margin={['base', 0]}>
                <Checkbox
                    label="Terms and Conditions"
                    options={[
                        {
                            label: (
                                <Row>
                                    <Col>
                                        I agree to the{' '}
                                        <LinkButton
                                            onClick={(ev) => {
                                                setShowTermsModal(
                                                    !showTermsModal,
                                                );
                                                ev.preventDefault();
                                            }}
                                        >
                                            Terms and Conditions
                                        </LinkButton>
                                    </Col>
                                </Row>
                            ),
                            value: true,
                        },
                    ]}
                    selectedValues={[termsAccepted]}
                    onChange={() => setTermsAccepted(!termsAccepted)}
                />
            </Box>

            <Box margin={['large', 0, 'base']}>
                <ReportCheckButton
                    checked={form.wants_insights}
                    onClick={() =>
                        changeField('wants_insights', !form.wants_insights)
                    }
                />
            </Box>

            {hasFieldError && (
                <Box margin={['large', 0]}>
                    <Alert theme="danger">
                        <p>
                            You can’t proceed to the next step because there are
                            issues that have been highlighted in the form above.
                        </p>
                    </Alert>
                </Box>
            )}
            {hasOtherError && (
                <Box margin={['large', 0]}>
                    <Alert theme="danger">
                        <p>
                            You were unable to activate the context because
                            there was an unexpected error. Please try again in a
                            few moments.{Object.values(errors).join(', ')}
                        </p>
                    </Alert>
                </Box>
            )}

            <Box margin={['large', 0, 0]}>
                <div className={styles['wizard-buttons']}>
                    <Button
                        theme="outline"
                        disabled={submitting}
                        onClick={() => onStepChange(steps.CHOOSE_DSP)}
                    >
                        Previous
                    </Button>
                    <Button
                        disabled={
                            !form.contact_name ||
                            !validEmail(form.contact_email) ||
                            !form.contact_company ||
                            !termsAccepted ||
                            submitting ||
                            hasFieldError
                        }
                        onClick={async () => {
                            try {
                                await onSubmit();
                            } catch {
                                return;
                            }
                            onStepChange(steps.SUMMARY);
                        }}
                    >
                        Activate Your Context
                        {submitting && (
                            <Box padding={[0, 0, 0, 'small']}>
                                <Spinner size="small" />
                            </Box>
                        )}
                    </Button>
                </div>
            </Box>

            {showTermsModal && (
                <TermsModal
                    accepted={termsAccepted}
                    onAccept={() => {
                        setTermsAccepted(true);
                        setShowTermsModal(false);
                    }}
                    onClose={() => setShowTermsModal(false)}
                >
                    {termsAndConditions}
                </TermsModal>
            )}
        </>
    );
};

export default ActivateStep;
